import { Link, useNavigate } from "@tanstack/react-router";
import { Boxes, ChevronRight } from "lucide-react";
import { useCallback, useState } from "react";
import {
  DeltaValue,
  MetaText,
  ScoreValue,
  StatusPill,
} from "@/components/plan-audit/AuditPrimitives";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { EnumSelect } from "@/components/shared/enum-select";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { DEFAULT_LIMIT, useDataGrid } from "@/hooks/use-data-grid";
import { type SolutionRow, useSolutions } from "@/hooks/use-plan-audit";
import { ENGAGEMENT_STAGE_OPTIONS, RUN_STATUS_OPTIONS } from "@/lib/enums";
import { latestRunText, NO_VALUE, rowStatus } from "@/lib/plan-audit-format";

/**
 * Solutions — a plain list, deliberately.
 *
 * First-pass score, latest score and the movement between them read straight off
 * the row; tiles and charts live on Reporting. That is the whole editorial point
 * of this screen, so resist adding summary widgets here.
 *
 * Layout follows the design's `.na-table` (ported into `.pa-table` in
 * styles/app-shell.css): the latest run reads as a sentence, its status sits in
 * its own column so the pill stays the width of its own label, and the row ends
 * in a chevron. Only `name` is server-sortable among the visible columns —
 * versions, run, and all three score figures are computed per row from that
 * solution's runs, so they cannot be keyset-sorted and carry no `sortKey`
 * (see the note in src/api/solutions.ts).
 */
export function SolutionsTable() {
  const navigate = useNavigate();
  const grid = useDataGrid({ defaultSort: "updatedAt" });
  // The engagement-stage filter lives outside useDataGrid, which owns a single
  // `status`, already taken by the run-status filter. It must reset pagination
  // itself: a cursor from the previous filter points into a different result set.
  const [stage, setStageValue] = useState("");
  const setStage = useCallback(
    (value: string) => {
      setStageValue(value);
      grid.resetPage();
    },
    [grid],
  );
  const { data, isLoading, isError, error, refetch } = useSolutions(
    grid.query,
    grid.pageRequest,
    stage,
  );

  if (isError) {
    return <ErrorState message={error.message} onRetry={() => void refetch()} />;
  }

  const rows = data?.data ?? [];

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SearchInput
          value={grid.searchInput}
          onValueChange={grid.setSearch}
          placeholder="Search solutions or sector"
          className="w-full sm:max-w-xs"
        />
        <EnumSelect
          value={grid.status}
          onValueChange={grid.setStatus}
          options={RUN_STATUS_OPTIONS}
          allOption={{ label: "Any run status" }}
          aria-label="Filter by run status"
          className="w-[170px]"
        />
      </div>

      <div className="pa-card pa-card-tight">
        <div className="pa-panel-head">
          <h3>All solutions</h3>
          <StageFilter value={stage} onChange={setStage} />
        </div>
        <DataTable
          className="pa-table rounded-none border-0"
          headers={[
            { label: "Solution", sortKey: "name" },
            { label: "Plan versions" },
            { label: "Latest run" },
            { label: "Status" },
            { label: "First pass", className: "text-right" },
            { label: "Latest", className: "text-right" },
            { label: "Movement", className: "text-right" },
            { label: "Open", srOnly: true, className: "w-10" },
          ]}
          sort={grid.sort}
          sortDir={grid.sortDir}
          onSort={grid.toggleSort}
          loading={isLoading}
          empty={
            <EmptyState
              icon={Boxes}
              title="No solutions match"
              description="Adjust the search or filters, or start a new audit."
              action={
                <Button asChild variant="outline">
                  <Link to="/new-audit">New audit</Link>
                </Button>
              }
            />
          }
        >
          {rows.map((row) => (
            <SolutionTableRow
              key={row.id}
              row={row}
              onOpen={() => {
                // A running audit belongs on its run screen; anything else on the
                // solution's own page. Sending a running audit to the version list
                // would show a row that is still changing with no progress on screen.
                if (row.latestRun?.status === "running" || row.latestRun?.status === "queued") {
                  void navigate({
                    to: "/runs/$reference",
                    params: { reference: row.latestRun.reference },
                  });
                  return;
                }
                void navigate({ to: "/solutions/$slug", params: { slug: row.slug } });
              }}
            />
          ))}
        </DataTable>
      </div>

      <CursorPagination
        className="mt-4"
        total={data?.total ?? 0}
        page={grid.page}
        limit={DEFAULT_LIMIT}
        hasNext={data?.hasNext ?? false}
        hasPrev={data?.hasPrev ?? false}
        onNext={() => grid.goNext(data?.nextCursor ?? null)}
        onPrev={() => grid.goPrev(data?.prevCursor ?? null)}
        loading={isLoading}
        itemLabel="solutions"
      />
    </>
  );
}

/**
 * The stage filter as pills, per the design. It is the same one-of-many choice
 * the dropdown made, so "All" is the empty value — no separate sentinel.
 */
function StageFilter({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const options = [{ value: "", label: "All" }, ...ENGAGEMENT_STAGE_OPTIONS];
  return (
    <fieldset className="pa-filter">
      <legend className="sr-only">Filter by engagement stage</legend>
      {options.map((option) => (
        <button
          key={option.value || "all"}
          type="button"
          className="pa-filter-chip"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </fieldset>
  );
}

function SolutionTableRow({ row, onOpen }: { row: SolutionRow; onOpen: () => void }) {
  const run = row.latestRun;

  return (
    <tr onClick={onOpen} className="cursor-pointer">
      <td>
        <div className="font-semibold text-[var(--fg-1)]">{row.name}</div>
        <MetaText>
          {row.sector} · {row.stage}
        </MetaText>
      </td>
      <td className="text-[var(--fg-2)] tabular-nums">
        {row.versionCount} version{row.versionCount === 1 ? "" : "s"}
      </td>
      <td
        className="text-[var(--fg-2)] whitespace-nowrap"
        title={run?.status === "failed" ? run.failureReason || undefined : undefined}
      >
        {latestRunText(run)}
      </td>
      <td>
        {run === null ? (
          <MetaText>{NO_VALUE}</MetaText>
        ) : (
          <StatusPill status={rowStatus(run.status, row.reauditDue)} />
        )}
      </td>
      <td className="text-right">
        <ScoreValue score={row.firstScore} muted />
      </td>
      <td className="text-right">
        <ScoreValue score={row.latestScore} size="row" />
      </td>
      <td className="text-right">
        {row.movement === null ? (
          <span
            className="text-[var(--fg-3)]"
            title={
              row.latestScore === null
                ? "No completed run has produced a score yet"
                : "Needs two scored runs to compare"
            }
          >
            {NO_VALUE}
          </span>
        ) : (
          <DeltaValue delta={row.movement} className="text-xs" />
        )}
      </td>
      <td className="text-right text-[var(--fg-3)]">
        <ChevronRight className="inline size-4" aria-hidden="true" />
      </td>
    </tr>
  );
}
